import { devLog, devError } from '../utils/devLogger';
/**
 * Fillable Templates Component for Learners
 * Displays fillable templates and allows learners to submit completed versions
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Modal,
  Form,
  Alert,
  ProgressBar,
  Spinner,
  Table
} from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import templateSubmissionService from '../services/templateSubmissionService';

const FillableTemplates = () => {
  const { currentUser, userProfile } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [submitFile, setSubmitFile] = useState(null);
  const [submitComments, setSubmitComments] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadTemplatesAndSubmissions();
  }, [currentUser, userProfile?.cohortId, userProfile?.programId]);

  const loadTemplatesAndSubmissions = async () => {
    try {
      setLoading(true);
      setError('');

      // Load all fillable templates
      const templatesData = await templateSubmissionService.getFillableTemplates();
      const learnerCohortId = userProfile?.cohortId;
      const learnerProgramId = userProfile?.programId;

      const filteredTemplates = templatesData.filter((template) => {
        const cohortIds = Array.isArray(template.cohortIds)
          ? template.cohortIds
          : (template.cohortId ? [template.cohortId] : []);

        if (!learnerCohortId) return false;
        if (!cohortIds.includes(learnerCohortId)) return false;
        if (learnerProgramId && template.programId && template.programId !== learnerProgramId) {
          return false;
        }
        return true;
      });

      setTemplates(filteredTemplates);

      // Load learner's submissions for all templates
      if (currentUser) {
        const submissionsData = await templateSubmissionService.getLearnerSubmissions(
          currentUser.uid
        );

        // Create a map of templateId -> latest submission
        const submissionsMap = {};
        submissionsData.forEach(submission => {
          if (
            !submissionsMap[submission.templateId] ||
            submission.submittedAt > submissionsMap[submission.templateId].submittedAt
          ) {
            submissionsMap[submission.templateId] = submission;
          }
        });

        setSubmissions(submissionsMap);
      }
    } catch (error) {
      devError('Error loading templates:', error);
      setError('Failed to load templates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = (template) => {
    if (template.fileURL) {
      window.open(template.fileURL, '_blank');
    } else if (template.url) {
      window.open(template.url, '_blank');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!submitFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const submissionData = {
        templateId: selectedTemplate.id,
        templateTitle: selectedTemplate.title,
        learnerId: currentUser.uid,
        learnerName: userProfile?.displayName || currentUser.email,
        comments: submitComments,
        dueDate: selectedTemplate.templateDueDate
      };

      const submissionResult = await templateSubmissionService.submitTemplate(
        submissionData,
        submitFile,
        (progress) => setUploadProgress(progress)
      );

      setSuccess('Template submitted successfully!');
      setShowSubmitModal(false);
      setSubmitFile(null);
      setSubmitComments('');
      setUploadProgress(0);

      if (submissionResult && submissionResult.templateId) {
        setSubmissions((prev) => ({
          ...prev,
          [submissionResult.templateId]: submissionResult
        }));
      } else {
        await loadTemplatesAndSubmissions();
      }
    } catch (error) {
      devError('Error submitting template:', error);
      setError(error.message || 'Failed to submit template. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const openSubmitModal = (template) => {
    setSelectedTemplate(template);
    setShowSubmitModal(true);
    setError('');
    setSuccess('');
  };

  const getSubmissionStatus = (templateId) => {
    const submission = submissions[templateId];
    if (!submission) return null;

    const statusConfig = {
      pending: { variant: 'warning', text: 'Pending Review' },
      approved: { variant: 'success', text: 'Approved' },
      rejected: { variant: 'danger', text: 'Rejected' },
      revision_requested: { variant: 'info', text: 'Revision Requested' }
    };

    return statusConfig[submission.status] || statusConfig.pending;
  };

  const isDueSoon = (dueDate) => {
    if (!dueDate) return false;
    const now = new Date();
    const due = new Date(dueDate);
    const daysUntilDue = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    return daysUntilDue <= 7 && daysUntilDue >= 0;
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const now = new Date();
    const due = new Date(dueDate);
    return due < now;
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading templates...</p>
      </div>
    );
  }

  return (
    <div>
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      {templates.length === 0 ? (
        <Alert variant="info">
          <strong>No Templates Available</strong>
          <p className="mb-0 mt-2">
            There are currently no fillable templates assigned. Check back later!
          </p>
        </Alert>
      ) : (
        <Row>
          {templates.map(template => {
            const submission = submissions[template.id];
            const status = getSubmissionStatus(template.id);
            const dueSoon = isDueSoon(template.templateDueDate);
            const overdue = isOverdue(template.templateDueDate);

            return (
              <Col key={template.id} md={6} lg={4} className="mb-4">
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <Card.Title className="mb-0">{template.title}</Card.Title>
                      {status && (
                        <Badge bg={status.variant}>{status.text}</Badge>
                      )}
                    </div>

                    {template.description && (
                      <Card.Text className="text-muted small">
                        {template.description}
                      </Card.Text>
                    )}

                    {template.templateInstructions && (
                      <Alert variant="light" className="small mb-2">
                        <strong>Instructions:</strong><br />
                        {template.templateInstructions}
                      </Alert>
                    )}

                    {template.templateDueDate && (
                      <div className="mb-2">
                        <Badge
                          bg={overdue ? 'danger' : dueSoon ? 'warning' : 'secondary'}
                          className="me-1"
                        >
                          Due: {new Date(template.templateDueDate).toLocaleDateString()}
                        </Badge>
                        {overdue && <Badge bg="danger">OVERDUE</Badge>}
                        {dueSoon && !overdue && <Badge bg="warning">Due Soon</Badge>}
                      </div>
                    )}

                    {submission && (
                      <div className="mb-3">
                        <small className="text-muted">
                          Submitted: {submission.submittedAt.toLocaleString()}
                        </small>
                        {submission.reviewComments && (
                          <Alert variant="info" className="mt-2 small mb-0">
                            <strong>Reviewer Comments:</strong><br />
                            {submission.reviewComments}
                          </Alert>
                        )}
                        {submission.grade && (
                          <div className="mt-2">
                            <Badge bg="primary">Grade: {submission.grade}</Badge>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="d-grid gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleDownloadTemplate(template)}
                      >
                        📥 Download Template
                      </Button>

                      <Button
                        variant={submission ? 'warning' : 'primary'}
                        size="sm"
                        onClick={() => openSubmitModal(template)}
                      >
                        📤 {submission ? 'Resubmit' : 'Submit Completed'} Template
                      </Button>

                      {submission && submission.fileURL && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => window.open(submission.fileURL, '_blank')}
                        >
                          👁️ View My Submission
                        </Button>
                      )}
                    </div>
                  </Card.Body>

                  <Card.Footer className="text-muted small">
                    Uploaded by {template.uploadedBy}
                  </Card.Footer>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {/* Submit Template Modal */}
      <Modal show={showSubmitModal} onHide={() => setShowSubmitModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>📤 Submit Completed Template</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {selectedTemplate && (
              <>
                {submissions[selectedTemplate.id] && (
                  <Alert variant="warning" className="small">
                    Resubmitting will replace your previous submission.
                  </Alert>
                )}
                <h5>{selectedTemplate.title}</h5>

                {selectedTemplate.templateInstructions && (
                  <Alert variant="info">
                    <strong>Instructions:</strong><br />
                    {selectedTemplate.templateInstructions}
                  </Alert>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Upload Completed Template *</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={(e) => setSubmitFile(e.target.files[0])}
                    required
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  />
                  <Form.Text className="text-muted">
                    Maximum file size: 10MB. Accepted formats: PDF, Word, Excel, PowerPoint
                  </Form.Text>
                </Form.Group>

                {submitFile && (
                  <Alert variant="success">
                    <strong>Selected:</strong> {submitFile.name} ({(submitFile.size / 1024 / 1024).toFixed(2)} MB)
                  </Alert>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Comments (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={submitComments}
                    onChange={(e) => setSubmitComments(e.target.value)}
                    placeholder="Add any comments or notes for your facilitator..."
                  />
                </Form.Group>

                {uploading && (
                  <ProgressBar
                    now={uploadProgress}
                    label={`${Math.round(uploadProgress)}%`}
                    animated
                    className="mb-3"
                  />
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSubmitModal(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={uploading || !submitFile}>
              {uploading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Uploading...
                </>
              ) : (
                'Submit Template'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default FillableTemplates;
